import IconButton from '@/_application/core/components/ui/icon-button';
import Link from '@/_application/core/components/ui/link';
import {useModalAction} from '@/_application/core/components/ui/modal/modal.context';
import {UserIcon} from '@/_application/icons/user-icon';
import {CustomerRoutes, Routes} from '@/config/routes';
import {BookFavoriteIcon} from '@/_application/icons/book-favorite';
import MiniSearchHeader from './kidsplaza-spring.desktop.mini-search-header';
import {MiniCartPopoverContent, useCartSummary, useCartTotalItems} from '@kidslabs/module-quote';
import {cn, Divider, FlatList, Fragment, Text} from '@kidslabs/mario-nextjs-react-ui-core';
import React, {ComponentProps, useCallback, useMemo} from 'react';
import {useSession} from 'next-auth/react';
import Logo from '@/_application/core/components/ui/logo';
import {usePathname} from 'next/navigation';
import {useTranslation} from 'next-i18next';
import {observer} from '@legendapp/state/react';
import {MiniCartItemsRenderer} from '@kidslabs/module-quote';
import {CartItemGroup} from '../../../../../../nova/modules/cart/components';
import {fadeInOut} from '@/lib/motion/fade-in-out';
export interface RightNavSections {
    iconProps: ComponentProps<typeof IconButton>;
    href: string;
}

export interface RenderRightActionParams {
    item: RightNavSections;
}

const Header: React.FC = observer(() => {
    const pathname = usePathname();
    const {t} = useTranslation('common');
    const {totalItems} = useCartSummary();
    const totalTitle = useMemo(
        () => t('text-product-count', {count: totalItems, defaultValue: '(có {{count}} sản phẩm)'}),
        [totalItems, t]
    );

    const isPaymentPage = pathname?.startsWith(Routes.cart) || pathname?.startsWith(Routes.checkout);
    const {status: sessionStatus} = useSession();
    const {openModal} = useModalAction();
    const isAuthenticated = sessionStatus === 'authenticated';

    const onOpenLoginView = useCallback(() => {
        if (!isAuthenticated) {
            openModal('LOGIN_VIEW');
        }
    }, [openModal, isAuthenticated]);

    const rightSections: RightNavSections[] = [
        {
            href: `${Routes.customer}/orders`,
            iconProps: {
                label: 'Lịch sử đơn hàng',
                icon: <BookFavoriteIcon className="h-5 w-5" />,
                onClick: onOpenLoginView,
                className: 'flex w-16 flex-col text-white xl:w-28',
                labelClassName: 'pt-1',
            },
        },
        {
            href: `${Routes.customer}/${CustomerRoutes.info}`,
            iconProps: {
                label: 'Tài khoản',
                icon: <UserIcon className="h-5 w-5" />,
                onClick: onOpenLoginView,
                className: 'flex w-14 flex-col text-white xl:w-16',
                labelClassName: 'pt-1',
            },
        },
    ];

    const renderRightAction = useCallback(
        ({item}: RenderRightActionParams) => {
            if (isAuthenticated) {
                return (
                    <Link href={item.href}>
                        <IconButton {...item.iconProps} />
                    </Link>
                );
            }

            return <IconButton {...item.iconProps} />;
        },
        [isAuthenticated],
    );

    const navClassName = useMemo(() => {
        return cn('flex h-full items-center pl-[1%]', {
            'justify-start gap-9': isPaymentPage,
            'justify-between gap-7': !isPaymentPage,
        });
    }, [isPaymentPage]);

    const NovaMiniCartItemsRenderer = useCallback(() => {
        return <MiniCartItemsRenderer CartItemGroupComponent={CartItemGroup} />;
    }, []);

    return (
        <header className="sticky left-0 top-0 z-50 h-auto w-full bg-KPZ_Yellow pb-2">
            <div className="relative ml-[calc((100vw-698px)/2)] h-20.75 border-b-2 border-solid border-white bg-KPZ_Blue-Dark lg:ml-[calc((100vw-950px)/2)] xl:ml-[calc((100vw-1196px)/2)]">
                <div className="absolute -left-8.5 top-0 z-1 box-content h-5 w-12 rounded-full border-b-2 border-l-2 border-solid border-white bg-KPZ_Blue-Dark" />
                <div className="absolute -left-12.5 top-4.5 z-2 h-16.25 w-50 rounded-full border-2 border-solid border-white bg-KPZ_Blue-Dark" />
                <div className="absolute -left-7.5 top-[1.5px] z-2 h-5.25 w-15 rounded-full border-solid border-white bg-KPZ_Blue-Dark" />
                <div className="relative z-10 h-full w-174.5 bg-KPZ_Blue-Dark lg:w-237.5 xl:w-299">
                    <nav className={navClassName}>
                        <div className="relative">
                            <Logo
                                logoProps={{
                                    width: 224,
                                    height: 84,
                                    sizes: '100%',
                                    title: 'KidsPlaza',
                                    alt: 'KidsPlaza',
                                }}
                                logoLink="/"
                            />
                        </div>
                        {!isPaymentPage ? (
                            <Fragment>
                                <MiniSearchHeader />

                                <div className="flex items-start gap-1! xl:gap-2! 2xl:gap-4!">
                                    <FlatList
                                        data={rightSections}
                                        renderItem={renderRightAction}
                                        contentContainerClassName="flex flex-row! gap-1! xl:gap-2! 2xl:gap-4!"
                                    />

                                    <MiniCartPopoverContent
                                        fadeInOut={fadeInOut}
                                        routes={Routes}
                                        MiniCartItemsRendererComponent={NovaMiniCartItemsRenderer}
                                    />
                                </div>
                            </Fragment>
                        ) : (
                            <div className="flex items-center gap-9 text-white text-sm font-bold">
                                <Divider orientation="vertical" thickness={1} className="h-12 bg-white" />
                                <Text className="text-xl font-bold">
                                    {t('text-mini-cart', 'Giỏ hàng')} {totalTitle}
                                </Text>
                            </div>
                        )}
                    </nav>
                </div>
            </div>
        </header>
    );
});

export default Header;
